import {
  BaseQueryFn,
  MutationDefinition,
  EndpointBuilder,
  QueryDefinition,
  InfiniteQueryDefinition,
} from '@reduxjs/toolkit/query';

import { transformCollectionResponse } from 'services/teamHeroApi.helpers';
import type {
  IGetMessageConversationsArgs,
  IPostMessageConversationArgs,
  IPutMessageConversationArgs,
  IPutMessageConversationReadUnreadArgs,
} from './messageConversations.api.types';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import type {
  ICollection,
  ICollectionApiResponse,
  IInfinitePaginationArgs,
} from 'services/api/api.types';
import type { IMessageConversation } from 'interfaces/MessageConversation.interface';

export const getMessageConversations = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetMessageConversationsArgs,
  BaseQueryFn,
  string,
  ICollection<IMessageConversation>
> =>
  build.query<ICollection<IMessageConversation>, IGetMessageConversationsArgs>({
    providesTags: ['MessageConversation'],
    query: (queryArg: IGetMessageConversationsArgs | undefined) => {
      const paginationParamObject = {
        participant: queryArg?.participant,
        page: queryArg?.page,
        itemsPerPage: queryArg?.itemsPerPage,
        pagination: queryArg?.pagination,
        ...queryArg?.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg?.filters);
      return {
        url: `/api/message_conversations${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IMessageConversation>
    ): ICollection<IMessageConversation> =>
      transformCollectionResponse<IMessageConversation>(response),
  });

export const getInfiniteMessageConversations = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): InfiniteQueryDefinition<
  IGetMessageConversationsArgs,
  IInfinitePaginationArgs,
  BaseQueryFn,
  string,
  ICollection<IMessageConversation>
> =>
  build.infiniteQuery<
    ICollection<IMessageConversation>,
    IGetMessageConversationsArgs,
    IInfinitePaginationArgs
  >({
    providesTags: ['MessageConversation'],
    infiniteQueryOptions: {
      // Must provide a default initial page param value
      initialPageParam: {
        page: 1,
        itemsPerPage: 10,
      },
      getNextPageParam: (
        lastPage,
        _allPages,
        { itemsPerPage, page: pageLastPage },
        _allPageParams
      ) => {
        const totalCount = lastPage?.totalCount;
        if (!totalCount || !itemsPerPage) {
          return undefined;
        }
        if (totalCount <= itemsPerPage * pageLastPage || totalCount === 0) {
          return undefined;
        }
        return { page: pageLastPage + 1, itemsPerPage };
      },
      getPreviousPageParam: (
        _firstPage,
        _allPages,
        { page: pageFirstPage, itemsPerPage },
        _allPageParams
      ) => {
        const prevPage = pageFirstPage - 1;
        if (prevPage < 1) {
          return undefined;
        }
        return { page: prevPage, itemsPerPage };
      },
    },
    query: ({ queryArg, pageParam: { page, itemsPerPage } }) => {
      const paginationParamObject = {
        page,
        itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return `api/message_conversations${paginationQuery}${filterQuery}`;
    },
    transformResponse: (
      response: ICollectionApiResponse<IMessageConversation>
    ): ICollection<IMessageConversation> =>
      transformCollectionResponse<IMessageConversation>(response),
  });

export const getMessageConversation = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<number, BaseQueryFn, string, IMessageConversation> =>
  build.query<IMessageConversation, number>({
    providesTags: ['MessageConversation'],
    query: (id) => {
      return {
        url: `/api/message_conversations/${id}`,
      };
    },
  });

export const postMessageConversation = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPostMessageConversationArgs,
  BaseQueryFn,
  string,
  IMessageConversation
> =>
  build.mutation<IMessageConversation, IPostMessageConversationArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags ? [] : ['MessageConversation'],
    query: (queryArg) => ({
      url: '/api/message_conversations',
      method: 'POST',
      body: queryArg.body,
    }),
  });

export const putMessageConversation = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPutMessageConversationArgs,
  BaseQueryFn,
  string,
  IMessageConversation
> =>
  build.mutation<IMessageConversation, IPutMessageConversationArgs>({
    invalidatesTags: ['MessageConversation'],
    query: (queryArg) => ({
      url: `/api/message_conversations/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const putMessageConversationReadUnread = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPutMessageConversationReadUnreadArgs,
  BaseQueryFn,
  string,
  IMessageConversation
> =>
  build.mutation<IMessageConversation, IPutMessageConversationReadUnreadArgs>({
    invalidatesTags: ['MessageConversation'],
    query: (queryArg) => ({
      url: `/api/message_conversations/${queryArg.id}/status`,
      method: 'PATCH',
      body: {
        updatedStatus: queryArg.body.status,
      },
    }),
  });

export const deleteMessageConversation = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<number, BaseQueryFn, string, void> =>
  build.mutation<void, number>({
    invalidatesTags: ['MessageConversation'],
    query: (id) => ({
      url: `/api/message_conversations/${id}`,
      method: 'DELETE',
    }),
  });
